import { useMutation } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { UPLOAD_ERROR_MESSAGE } from '@/constants/messages';
import useAppToast from '@/hooks/useAppToast';
import {
  confirmUpload,
  presignUpload,
  putFileToPresignedUrl,
} from '@/services/upload/uploadService';
import type {
  UploadContext,
  UploadPortal,
  UploadPortalParams,
  UploadTokenMetadata,
} from '@/types/upload';
import { useUploadPortal } from './useUploadPortal';
import { getValidatorForContext } from './uploadValidators';

export interface UseUploadFileOptions {
  /** Defaults to the current route's portal (auto-detected). */
  portal?: UploadPortal;
  siteType?: string;
  clinicNumber?: string;
  /** Upload bucket key — maps to a context config on BE. */
  context: UploadContext;
}

export type UploadFileResult = UploadTokenMetadata;

/**
 * High-level hook that performs the full 3-step direct upload flow:
 *   1. Ask BE for a presigned PUT URL
 *   2. PUT the file directly to S3/MinIO (bypasses Laravel)
 *   3. Ask BE to verify & confirm the upload
 *
 * Exposes upload progress (0..1) and an AbortController so callers can cancel.
 *
 * @example
 *   const { upload, isUploading, progress, cancel } = useUploadFile({ context: 'insurer_attention_attachment' });
 *   const token = await upload(file);
 */
export const useUploadFile = (options: UseUploadFileOptions) => {
  const { portal, params } = useUploadPortal({
    portal: options.portal,
    siteType: options.siteType,
    clinicNumber: options.clinicNumber,
  });
  const toast = useAppToast();

  const [progress, setProgress] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const mutation = useMutation<UploadFileResult, Error, File>({
    mutationFn: async (file: File) => {
      // Clear previous validation error
      setValidationError(null);

      // Validate file before upload
      const validator = getValidatorForContext(options.context);
      if (validator) {
        const error = validator(file);
        if (error) {
          setValidationError(error);
          throw new Error(error);
        }
      }

      setProgress(0);
      abortRef.current?.abort();
      abortRef.current = new AbortController();

      return await runFullUpload({
        portal,
        params,
        context: options.context,
        file,
        onProgress: setProgress,
        signal: abortRef.current.signal,
      });
    },
    onError: error => {
      // Skip toast when user cancels intentionally.
      if (error instanceof Error && error.name === 'CanceledError') return;

      // Skip if validation error (error message is stored in validationError state)
      if (
        error instanceof Error &&
        (error.message.includes('拡張子') || error.message.includes('容量'))
      ) {
        return;
      }

      // Every other failure (presign/PUT/confirm — including 401 auth token expiry
      // and 422 upload-token-expired) surfaces the same generic upload error so the
      // user sees a consistent message.
      toast.error(UPLOAD_ERROR_MESSAGE);
    },
    meta: { suppressNetworkToast: true },
  });

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const clearValidationError = useCallback(() => {
    setValidationError(null);
  }, []);

  return {
    upload: mutation.mutateAsync,
    cancel,
    progress,
    isUploading: mutation.isPending,
    error: mutation.error,
    validationError,
    clearValidationError,
    data: mutation.data,
    reset: mutation.reset,
  };
};

/**
 * Standalone variant (no hook) — useful inside event handlers that aren't inside components.
 */
export const runFullUpload = async (input: {
  portal: UploadPortal;
  params: UploadPortalParams;
  context: UploadContext;
  file: File;
  onProgress?: (ratio: number) => void;
  signal?: AbortSignal;
}): Promise<UploadTokenMetadata> => {
  const { portal, params, context, file, onProgress, signal } = input;

  const presign = await presignUpload(portal, params, {
    context,
    filename: file.name,
    content_type: file.type || 'application/octet-stream',
    size: file.size,
  });

  await putFileToPresignedUrl(
    presign.upload_url,
    file,
    presign.required_headers,
    onProgress,
    signal
  );

  return await confirmUpload(portal, params, presign.token);
};
